import { Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize } from 'rxjs';

import { Environment } from '../../classes/environment';
import { PaymentService, PaymentStatusDto, StripePriceDto } from '../../services/payment.service';
import { StripeLoaderService, StripeCheckout } from '../../services/stripe-loader.service';
import { PlanPricingService } from '../../services/plan-pricing.service';
import { ReturnUrlService } from '../../services/return-url.service';
import { PLANS, Plan } from '../authentication/authentication-plans/plans.data';

@Component({
  selector: 'app-payments',
  templateUrl: './payments.component.html',
  styleUrls: ['./payments.component.scss'],
})
export class PaymentsComponent implements OnInit {
  plans: Plan[] = PLANS.map((plan) => ({ ...plan }));
  loadingPlanId: string | null = null;
  errorMessage = '';
  statusError = '';
  statusLoading = false;
  paymentStatus: PaymentStatusDto | null = null;

  constructor(
    @Inject('environment') public environment: Environment,
    private paymentService: PaymentService,
    private stripeLoader: StripeLoaderService,
    private returnUrl: ReturnUrlService,
    private snackBar: MatSnackBar,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private planPricing: PlanPricingService
  ) {}

  ngOnInit(): void {
    this.loadStripePrices();
    this.fetchPaymentStatus(true);
    this.handleReturnFromStripe();
  }

  get stripeConfigured(): boolean {
    return !!this.environment.stripePublishableKey;
  }

  startCheckout(plan: Plan) {
    if (!plan.stripePriceId) {
      this.router.navigate(['/Kontakt']);
      return;
    }

    if (!this.stripeConfigured) {
      this.snackBar.open('Stripe ist aktuell nicht konfiguriert. Bitte kontaktieren Sie den Support.', 'Hinweis');
      return;
    }

    this.errorMessage = '';
    this.loadingPlanId = plan.id;

    const successUrl = this.returnUrl.buildReturnUrl(
      this.environment.stripeSuccessUrl,
      '/Zahlungen?status=success'
    );
    const cancelUrl = this.returnUrl.buildReturnUrl(
      this.environment.stripeCancelUrl,
      '/Zahlungen?status=cancel'
    );

    this.stripeLoader
      .load(this.environment.stripePublishableKey)
      .then((stripe) => {
        if (!stripe) {
          this.errorMessage = 'Stripe konnte nicht initialisiert werden. Bitte versuchen Sie es erneut.';
          this.loadingPlanId = null;
          return null;
        }

        this.paymentService
          .createCheckoutSession(plan.stripePriceId!, 1, successUrl, cancelUrl)
          .pipe(finalize(() => (this.loadingPlanId = null)))
          .subscribe({
            next: (sessionId) => this.forwardToStripe(sessionId, stripe),
            error: (error) => {
              console.error('Stripe Checkout Session konnte nicht erstellt werden.', error);
              this.errorMessage =
                'Die Stripe-Zahlung konnte nicht vorbereitet werden. Bitte versuchen Sie es später erneut.';
            },
          });

        return null;
      })
      .catch((error) => {
        console.error('Fehler beim Laden von Stripe', error);
        this.errorMessage = 'Stripe konnte nicht geladen werden. Bitte versuchen Sie es zu einem späteren Zeitpunkt erneut.';
        this.loadingPlanId = null;
      });
  }

  private loadStripePrices(): void {
    this.paymentService.getStripePrices().subscribe({
      next: (prices) => this.applyStripePrices(prices),
      error: (error) => {
        console.error('Stripe Preise konnten nicht geladen werden.', error);
      },
    });
  }

  private applyStripePrices(prices: StripePriceDto[]): void {
    this.plans = this.planPricing.applyPrices(this.plans, prices);
  }

  private handleReturnFromStripe(): void {
    this.activatedRoute.queryParamMap.subscribe((params) => {
      const status = params.get('status');
      if (status === 'success') {
        this.fetchPaymentStatus(true);
      }
      if (status === 'cancel') {
        this.snackBar.open('Zahlung abgebrochen. Sie können es erneut versuchen.', '', { duration: 4000 });
      }
    });
  }

  private fetchPaymentStatus(showToast = false): void {
    this.statusLoading = true;
    this.statusError = '';

    this.paymentService
      .getPaymentStatus()
      .pipe(finalize(() => (this.statusLoading = false)))
      .subscribe({
        next: (paymentStatus: PaymentStatusDto) => {
          this.paymentStatus = paymentStatus;

          if (showToast && paymentStatus?.active) {
            this.snackBar.open('Zahlung bestätigt. Plan wurde aktiviert.', '', { duration: 4000 });
          } else if (showToast && !paymentStatus?.active) {
            this.errorMessage = 'Zahlung konnte nicht bestätigt werden. Bitte wenden Sie sich an den Support.';
          }
        },
        error: (error) => {
          console.error('Payment-Status konnte nicht geladen werden.', error);
          this.statusError = 'Payment-Status konnte nicht geladen werden. Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.';
        },
      });
  }

  getPlanDisplayName(status: PaymentStatusDto | null): string {
    if (!status?.planId) {
      return '';
    }

    const planId = status.planId.toLowerCase();
    const match = this.plans.find(
      (plan) =>
        plan.id === planId ||
        plan.stripePriceId?.toLowerCase() === planId ||
        plan.stripeProductId?.toLowerCase() === planId ||
        plan.stripeProductName?.toLowerCase() === planId
    );

    return match?.title || status.planId;
  }

  private forwardToStripe(sessionId: string, stripe: StripeCheckout) {
    stripe
      .redirectToCheckout({ sessionId })
      .then((result) => {
        if (result.error?.message) {
          this.errorMessage = result.error.message;
        }
      })
      .catch((error) => {
        console.error('Fehler bei der Weiterleitung zu Stripe', error);
        this.errorMessage = 'Die Weiterleitung zu Stripe ist fehlgeschlagen. Bitte versuchen Sie es erneut.';
      });
  }
}
